import FileInput from '../../ui/FileInput/FileInput';
import { UploadButtonText, UploadFileButton } from '../FeedbackStyled';
import { Space } from '@gr-monorepo/common/ui';
import { FilePlate } from '../../ui/FilePlate/FilePlate';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { ReactComponent as Attach } from '../icons/attach.svg';
import { FeedbackFileUploadPrompt, FeedbackFileUploadWrapper } from './FeedbackFileUploadStyled';
import { useIndexGenerator } from '../../../../hooks/useIndexer';
import { FeedbacksApi } from '../../../../api';

type FeedbackFileUploadProps = {
  onListUpdate?: (FileListIds: number[]) => void;
  isErrorOrLoading?: (err: boolean) => void;
};

enum FileErrors {
  UnsupportedType = 'Расширение файла не поддерживается',
  FileTooLarge = 'Размер файла больше 10 Мб',
  UploadError = 'Ошибка при загрузке файла на сервер',
}

export type FileLoadingStoreType = {
  operationId: number;
  attachId: number | null;
  file: File;
  status: 'loading' | 'ready' | 'error';
  error: FileErrors | null;
  isErrored: boolean;
  isLoading: boolean;
}[];

const maxFileSizeInBytes = Math.round(1024 * 1024 * 10.1); //10.1 MiB
const maxFilesCount = 5;

export const FeedbackFileUpload = forwardRef(
  (
    { ...props }: FeedbackFileUploadProps,
    ref?: React.ForwardedRef<{ removeFilesCallback: () => void } | undefined>,
  ) => {
    const supportedFileExtensions = ['.jpeg', '.jpg', '.png', '.pdf', '.xlsx', '.xls', '.doc', '.docx'];
    const [files, setFiles] = useState<FileLoadingStoreType>([]);
    const indexGen = useIndexGenerator(1);
    const errors = useMemo(
      () =>
        Array.from(
          new Set<FileErrors>(
            files.filter((file) => file.error !== null).map((file) => file.error ?? FileErrors.UnsupportedType),
          ),
        ),
      [files],
    );

    useImperativeHandle(ref, () => ({
      removeFilesCallback() {
        handleFileRemoving(null);
      },
    }));

    const handleFileUpload = (uploadedFiles: File[]) => {
      const preparedFiles: FileLoadingStoreType = [];

      for (let i = 0; i < Math.min(uploadedFiles.length, maxFilesCount - files.length); i++) {
        const fileCandidate = uploadedFiles[i];
        const error = fileCandidate.size > maxFileSizeInBytes ? FileErrors.FileTooLarge : null;
        const fileIndex = indexGen();
        const formData = new FormData();
        formData.append('file', fileCandidate);
        if (!error) {
          FeedbacksApi.uploadFeedbackAttachment({ file: { value: fileCandidate } })
            .then((result) =>
              setFiles((files) =>
                files.map((file) =>
                  file.operationId === fileIndex
                    ? { ...file, attachId: result.id, status: 'ready', error: null, isLoading: false, isErrored: false }
                    : { ...file },
                ),
              ),
            )
            .catch(() =>
              setFiles((files) =>
                files.map((file) =>
                  file.operationId === fileIndex
                    ? { ...file, status: 'error', error: FileErrors.UploadError, isLoading: false, isErrored: true }
                    : { ...file },
                ),
              ),
            );
        }
        preparedFiles.push({
          file: fileCandidate,
          error: error,
          operationId: fileIndex,
          attachId: null,
          status: error ? 'error' : 'loading',
          isLoading: !error,
          isErrored: !!error,
        });
      }

      setFiles([...files, ...preparedFiles]);
    };

    const handleFileRemoving = (opIdForRemoving: number | null) => {
      if (opIdForRemoving) {
        setFiles((files) => files.filter((file) => file.operationId !== opIdForRemoving));
      } else {
        setFiles([]);
      }
    };

    useEffect(() => {
      const initial: { isLoadingOrError: boolean; attachIds: number[] } = { isLoadingOrError: false, attachIds: [] };

      const reduced = files.reduce(
        (prev, curr) => ({
          isLoadingOrError: prev.isLoadingOrError || !!curr.error || curr.isLoading,
          attachIds: curr.attachId ? [...prev.attachIds, curr.attachId] : [...prev.attachIds],
        }),
        initial,
      );

      props.isErrorOrLoading?.(reduced.isLoadingOrError);
      props.onListUpdate?.(reduced.attachIds);
    }, [files]);

    return (
      <>
        <FeedbackFileUploadWrapper>
          <FileInput
            onChange={handleFileUpload}
            dropProps={{ accept: supportedFileExtensions }}
            disabled={files.length >= maxFilesCount}
          >
            <UploadFileButton isDisabled={files.length >= maxFilesCount}>
              <Attach />
              <Space size={4} axis={'horizontal'} />
              <UploadButtonText>{'ПРИКРЕПИТЬ ФАЙЛ'}</UploadButtonText>
            </UploadFileButton>
          </FileInput>
          <FeedbackFileUploadPrompt>
            {
              'Вы можете загрузить не более пяти файлов с расширением .docx, .doc, .xlsx, .xls, .pdf, .png, .jpg размером до 10 Мб каждый'
            }
          </FeedbackFileUploadPrompt>
        </FeedbackFileUploadWrapper>
        <Space size={12} axis={'vertical'} />
        <FilePlate files={files} onRemove={handleFileRemoving} errors={errors} />
      </>
    );
  },
);
